/**
 * Owner: Masa Bayu
 * File: color-system.ts
 * Date:Time: 2026-09-13 21:16
 * Description: 22C QXColorSystem — swatches, themes, refs, validation.
 */
import { isQXColorRole } from './color-role';
import { createQXColorSwatch, isQXColorSwatch } from './color-swatch';
import { createQXTheme, createQXThemeReference, isQXTheme, isQXThemeReference, } from './color-theme';
export const QX_COLOR_SYSTEM_MODEL_VERSION = '1.0.0';
export const QX_COLOR_SYSTEM_FORBIDDEN_KEYS = [
    'react',
    'component',
    'css',
    'className',
    'style',
    'dom',
    'html',
    'hex',
    'rgb',
    'hsl',
    'gradient',
    'opacity',
    'shadow',
    'renderer',
    'layout',
    'qxw',
    'qxp',
    'widget',
];
function assertNoForbidden(record) {
    for (const key of QX_COLOR_SYSTEM_FORBIDDEN_KEYS) {
        if (key in record) {
            throw new Error(`Color system must not include forbidden field "${key}"`);
        }
    }
}
export function createQXColorSystem(options) {
    assertNoForbidden({ ...options });
    const swatches = options.swatches.map((swatch) => createQXColorSwatch(swatch));
    const swatchIds = new Set();
    for (const swatch of swatches) {
        if (swatchIds.has(swatch.id)) {
            throw new Error(`Duplicate color swatch id: ${swatch.id}`);
        }
        swatchIds.add(swatch.id);
    }
    const themes = options.themes.map((theme) => createQXTheme(theme));
    const themeIds = new Set();
    for (const theme of themes) {
        if (themeIds.has(theme.id)) {
            throw new Error(`Duplicate theme id: ${theme.id}`);
        }
        themeIds.add(theme.id);
        for (const mapping of theme.mappings) {
            if (!swatchIds.has(mapping.swatchId)) {
                throw new Error(`Theme "${theme.id}" role "${mapping.role}" references unknown swatchId "${mapping.swatchId}"`);
            }
        }
    }
    const defaultTheme = createQXThemeReference(options.defaultTheme.themeId);
    if (!themeIds.has(defaultTheme.themeId)) {
        throw new Error(`Default theme reference unknown themeId "${defaultTheme.themeId}"`);
    }
    return Object.freeze({
        modelVersion: QX_COLOR_SYSTEM_MODEL_VERSION,
        swatches: Object.freeze(swatches),
        themes: Object.freeze(themes),
        defaultTheme,
    });
}
export function isQXColorSystem(value) {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        return false;
    }
    const record = value;
    return (record.modelVersion === QX_COLOR_SYSTEM_MODEL_VERSION &&
        Array.isArray(record.swatches) &&
        Array.isArray(record.themes) &&
        isQXThemeReference(record.defaultTheme));
}
export function validateQXColorSystem(value) {
    if (!isQXColorSystem(value)) {
        throw new Error('Invalid color system');
    }
    assertNoForbidden({ ...value });
    for (const swatch of value.swatches) {
        if (!isQXColorSwatch(swatch)) {
            throw new Error('Invalid color swatch');
        }
    }
    for (const theme of value.themes) {
        if (!isQXTheme(theme)) {
            throw new Error('Invalid theme');
        }
    }
    createQXColorSystem(value);
}
/**
 * Immutable default-theme update — returns a new frozen system.
 */
export function withQXColorSystemDefaultTheme(system, themeId) {
    return createQXColorSystem({
        swatches: system.swatches,
        themes: system.themes,
        defaultTheme: createQXThemeReference(themeId),
    });
}
export function getQXTheme(system, reference = system.defaultTheme) {
    const theme = system.themes.find((item) => item.id === reference.themeId);
    if (!theme) {
        throw new Error(`Unknown theme reference "${reference.themeId}"`);
    }
    return theme;
}
/**
 * Deterministic role → swatchId resolution for a theme reference.
 */
export function resolveQXColorRole(system, role, reference = system.defaultTheme) {
    if (!isQXColorRole(role)) {
        throw new Error(`Invalid color role: ${String(role)}`);
    }
    const theme = getQXTheme(system, reference);
    const mapping = theme.mappings.find((item) => item.role === role);
    if (!mapping) {
        throw new Error(`Theme "${theme.id}" does not map color role "${role}"`);
    }
    return mapping.swatchId;
}
export function listQXColorRolesInTheme(system, reference = system.defaultTheme) {
    return getQXTheme(system, reference).mappings.map((mapping) => mapping.role);
}
//# sourceMappingURL=color-system.js.map